import React, { useState, useEffect } from 'react';
import { X, Loader2, Copy, CheckCircle, XCircle } from 'lucide-react';
import { useClerk, useOrganization } from '@clerk/clerk-react';
import { getApiBaseUrl, getOrgSlug } from '../config/backend.config';
import './ReportGenerationProgressModal.css';

function ReportGenerationProgressModal({ isOpen, onClose, onComplete, jobId, reportName, selectedWorkspace, progressData }) {
  const [logContent, setLogContent] = useState('');
  const [isComplete, setIsComplete] = useState(false);
  const [isError, setIsError] = useState(false);
  const [copiedStates, setCopiedStates] = useState({});
  const [isCancelling, setIsCancelling] = useState(false);
  const completionCalledRef = React.useRef(false); // Track if onComplete was already called for this job
  const clerk = useClerk();
  const { organization } = useOrganization();

  // Close modal without affecting left panel status
  const handleCloseModal = () => {
    onClose();
  };

  // Cancel the report generation
  const handleCancelGeneration = async () => {
    if (!jobId || !selectedWorkspace?.workspace_id || !clerk?.session || !organization) {
      return;
    }

    setIsCancelling(true);
    try {
      const sessionToken = await clerk.session.getToken();
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);
      const queryParams = new URLSearchParams({
        workspace_id: selectedWorkspace.workspace_id
      }).toString();

      const response = await fetch(
        `${apiBaseUrl}/saved-reports/job/${jobId}/cancel?${queryParams}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.ok) {
        setLogContent(prev => prev + '\n\n[CANCELLED] Report generation was cancelled by user.');
        setIsComplete(true);
        setIsError(true);

        // Call completion callback to update UI
        if (onComplete) {
          onComplete(jobId);
        }
      }
    } catch (error) {
      console.error('Error cancelling job:', error);
      setLogContent(prev => prev + '\n\n[ERROR] Failed to cancel report generation.');
    } finally {
      setIsCancelling(false);
    }
  };

  // Reset all state when jobId changes (new report generation started)
  React.useEffect(() => {
    completionCalledRef.current = false;
    setIsComplete(false);
    setIsError(false);
    setLogContent('');
    setIsCancelling(false);
  }, [jobId]);

  // Use WebSocket progress data instead of polling
  useEffect(() => {
    if (!progressData) return;

    // Update log content from accumulated log lines
    if (progressData.logLines && progressData.logLines.length > 0) {
      setLogContent(progressData.logLines.join('\n'));
    }

    // Update completion status
    if (progressData.status === 'complete') {
      setIsComplete(true);
      if (!progressData.success) {
        setIsError(true);
      }
      // Call completion callback only once per job
      if (onComplete && !completionCalledRef.current) {
        completionCalledRef.current = true;
        onComplete(jobId);
      }
    } else if (progressData.status === 'cancelled') {
      setIsComplete(true);
      setIsError(true);
    }
  }, [progressData, jobId, onComplete]);

  // One-time REST fetch on mount as reconnection fallback
  // (in case WebSocket disconnected and reconnected mid-generation)
  useEffect(() => {
    if (!isOpen || !jobId || !selectedWorkspace?.workspace_id || !clerk?.session || !organization) {
      return;
    }

    // Only fetch if we don't already have data from WebSocket
    if (progressData?.logLines?.length > 0) {
      return;
    }

    const fetchInitialState = async () => {
      try {
        const sessionToken = await clerk.session.getToken();
        const orgSlug = getOrgSlug(organization);
        const apiBaseUrl = getApiBaseUrl(orgSlug);
        const queryParams = new URLSearchParams({
          workspace_id: selectedWorkspace.workspace_id
        }).toString();

        // Fetch log
        const logResponse = await fetch(
          `${apiBaseUrl}/saved-reports/job/${jobId}/log?${queryParams}`,
          {
            headers: {
              'Authorization': `Bearer ${sessionToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (logResponse.ok) {
          const logData = await logResponse.json();
          if (logData.success && logData.log) {
            setLogContent(logData.log);
          }
        }

        // Fetch status
        const statusResponse = await fetch(
          `${apiBaseUrl}/saved-reports/job/${jobId}/status?${queryParams}`,
          {
            headers: {
              'Authorization': `Bearer ${sessionToken}`,
              'Content-Type': 'application/json',
            },
          }
        );

        if (statusResponse.ok) {
          const statusData = await statusResponse.json();
          if (statusData.status === 'complete') {
            setIsComplete(true);
            if (!statusData.success) {
              setIsError(true);
            }
            // Call completion callback only once per job
            if (onComplete && !completionCalledRef.current) {
              completionCalledRef.current = true;
              onComplete(jobId);
            }
          }
        }
      } catch (error) {
        console.error('Error fetching initial state:', error);
      }
    };

    fetchInitialState();
  }, [isOpen, jobId, selectedWorkspace?.workspace_id, clerk?.session, organization, progressData?.logLines?.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCopy = (text, section) => {
    navigator.clipboard.writeText(text);
    setCopiedStates({ ...copiedStates, [section]: true });
    setTimeout(() => {
      setCopiedStates({ ...copiedStates, [section]: false });
    }, 2000);
  };

  return (
    <>
      {isOpen && (
        <div className="report-generation-modal-overlay" onClick={handleCloseModal}>
          <div className="report-generation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="report-generation-modal-header">
              <div className="report-generation-modal-title">
                {isComplete ? (
                  <>
                    {isError ? (
                      <span className="status-error">❌ Save Failed</span>
                    ) : (
                      <span className="status-success">✅ Report Saved</span>
                    )}
                  </>
                ) : (
                  <>
                    <Loader2 size={20} className="spinner" />
                    <span>Generating Report</span>
                  </>
                )}
              </div>
              <button className="close-button" onClick={handleCloseModal} title={isComplete ? "Close" : "Close (generation will continue)"}>
                <X size={20} />
              </button>
            </div>

            <div className="report-generation-modal-body">
              <div className="report-info">
                <h3>{reportName}</h3>
                <p className="report-status">
                  {isComplete ? (
                    isError ? 'Report generation failed' : 'Report generated successfully'
                  ) : (
                    'Generating report, please wait...'
                  )}
                </p>
              </div>

              <div className="progress-section">
                <div className="progress-header">
                  <h4>Generation Log</h4>
                  {logContent && (
                    <button
                      className="copy-button"
                      onClick={() => handleCopy(logContent, 'log')}
                      title="Copy log"
                    >
                      {copiedStates.log ? <CheckCircle size={14} /> : <Copy size={14} />}
                    </button>
                  )}
                </div>
                <div className="progress-log">
                  <pre>{logContent || 'Waiting for logs...'}</pre>
                </div>
              </div>

              <div className="progress-actions">
                {isComplete ? (
                  <>
                    <div></div>
                    <button className={`primary-button ${!isError ? 'success-button' : ''}`} onClick={onClose}>
                      {isError ? 'Close' : 'Done'}
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="cancel-button-secondary"
                      onClick={handleCancelGeneration}
                      disabled={isCancelling}
                    >
                      {isCancelling ? (
                        <>
                          <Loader2 size={14} className="spinner" />
                          <span>Cancelling...</span>
                        </>
                      ) : (
                        <>
                          <XCircle size={14} />
                          <span>Cancel</span>
                        </>
                      )}
                    </button>
                    <button className="primary-button" onClick={onClose}>
                      Close
                    </button>
                  </>
                )}
              </div>
              {!isComplete && (
                <p className="progress-hint">You can safely close this dialog — generation will continue in the background.</p>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default ReportGenerationProgressModal;
